import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  LinearProgress,
  Fade,
  Zoom,
  Tooltip,
  Chip,
  Alert
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  MusicNote as MusicIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import { useQuizScoring } from '../../../hooks/useQuizScoring';

const QuizletScreen = ({ 
  config, 
  quizData, 
  onStartQuiz, 
  completedQuizlets: propCompletedQuizlets = new Set(),
  quizletResults: propQuizletResults = {},
  mainTimer = null  
}) => {
  const [completedQuizlets, setCompletedQuizlets] = useState(propCompletedQuizlets);
  const [, setCurrentQuizletIndex] = useState(null); // null = no active quizlet
  const [quizletResults, setQuizletResults] = useState(propQuizletResults);
  const [selectedQuizletIndex, setSelectedQuizletIndex] = useState(null); // Track selected but not started quizlet

  const { formatScore } = useQuizScoring(quizData?.difficulty);

  // Use centralized main timer from QuizMaster
  const mainTimeLeft = mainTimer?.timeLeft || 0;
  const mainTimerRunning = mainTimer?.isRunning || false;

  // Sync props with local state
  useEffect(() => {
    // Ensure we always have a Set
    if (propCompletedQuizlets instanceof Set) {
      setCompletedQuizlets(propCompletedQuizlets);
    } else {
      setCompletedQuizlets(new Set(propCompletedQuizlets || []));
    }
  }, [propCompletedQuizlets]);

  useEffect(() => {
    setQuizletResults(propQuizletResults || {});
  }, [propQuizletResults]);

  // Reset selection when returning to the screen
  useEffect(() => {
    setSelectedQuizletIndex(null);
    setCurrentQuizletIndex(null);
  }, []); // Only on mount

  // Main timer is now managed centrally by QuizMaster

  // No global timer - timers run only during active quizlets

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleQuizletSelect = (quizletIndex) => {
    if (completedQuizlets.has(quizletIndex)) return; // Can't select completed quizlets
    setSelectedQuizletIndex(quizletIndex);
  };

  const handleQuizletStart = () => {
    if (selectedQuizletIndex === null) return;
    
    setCurrentQuizletIndex(selectedQuizletIndex);
    onStartQuiz(selectedQuizletIndex);
  };

  const getQuizletStatus = (index) => {
    if (completedQuizlets.has(index)) {
      return 'completed';
    }
    if (index === selectedQuizletIndex) {
      return 'selected';
    }
    return 'available';
  };

  const getTotalScore = () => {
    return Object.values(quizletResults).reduce((total, result) => total + (result?.totalScore || 0), 0);
  };

  const getTotalQuestionsAnswered = () => {
    return Object.values(quizletResults).reduce((total, result) => total + (result?.totalQuestions || 0), 0);
  };

  const getTotalCorrect = () => {
    return Object.values(quizletResults).reduce((total, result) => total + (result?.correctAnswers || 0), 0);
  };

  const isQuizComplete = () => {
    return completedQuizlets.size === quizData?.quiz_data?.length;
  };

  const getRemainingQuizlets = () => {
    return quizData?.quiz_data?.length - completedQuizlets.size;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'selected': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CompletedIcon />;
      case 'selected': return <PlayIcon />;
      default: return <PlayIcon />;
    }
  };

  // Loading state
  if (!quizData || !quizData.quiz_data) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom>
            Loading Quiz Data...
          </Typography>
          <Box sx={{ width: 200, mx: 'auto' }}>
            <LinearProgress />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Select a Quizlet to Begin
        </Typography>
        
        {/* Main Timer Display */}
        <Card sx={{ maxWidth: 300, mx: 'auto', mb: 3, bgcolor: mainTimeLeft <= 60 ? 'error.light' : mainTimerRunning ? 'warning.light' : 'info.light' }}>
          <CardContent sx={{ py: 2, textAlign: 'center' }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <ClockIcon sx={{ 
                color: 'black',
                fontSize: 28 
              }} />
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ 
                  color: 'black'
                }}>
                  {formatTime(mainTimeLeft)}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: 'black' 
                }}>
                  {mainTimerRunning ? 'Time Remaining' : 'Total Quiz Time'}
                </Typography>
              </Box>
            </Box>
            {mainTimeLeft <= 60 && mainTimerRunning && (
              <Typography variant="caption" color="error.dark" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
                ⚠️ Time Running Out!
              </Typography>
            )}
          </CardContent>
        </Card>
        
        {selectedQuizletIndex !== null && (
          <Box>
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
              Quizlet {selectedQuizletIndex + 1} Selected - Click "Start Quizlet" to begin!
            </Typography>
            {!mainTimerRunning && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                ⏱️ Main timer will start when you begin your first quizlet
              </Typography>
            )}
          </Box>
        )}
        
        {/* Progress Summary */}
        {/* <Card sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quiz Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(completedQuizlets.size / (quizData?.quiz_data?.length || 1)) * 100}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                mb: 1
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {completedQuizlets.size} of {quizData?.quiz_data?.length} quizlets completed
            </Typography>
          </CardContent>
        </Card> */}

        {/* Score Summary */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Chip
                icon={<MusicIcon />}
                label={`${completedQuizlets.size}/${quizData?.quiz_data?.length} Songs`}
                color="primary"
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Chip
                label={`${formatScore(getTotalScore())} Points`}
                color="secondary"
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Chip
                label={`${getTotalCorrect()}/${getTotalQuestionsAnswered()} Correct`}
                color="info"
                variant="outlined"
              />
            </Grid>
            {/* {mainTimerRunning && (
              <Grid item>
                <Chip
                  icon={<TimerIcon />}
                  label={`${formatTime(mainTimerDuration - mainTimeLeft)} Elapsed`}
                  color={mainTimeLeft <= 60 ? "error" : "default"}
                  variant="outlined"
                />
              </Grid>
            )} */}
          </Grid>
        </Box>
      </Box>

      {/* Timer Warning */}
      {mainTimerRunning && mainTimeLeft <= 120 && mainTimeLeft > 0 && (
        <Alert 
          severity={mainTimeLeft <= 60 ? "error" : "warning"} 
          sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}
        >
          <Typography variant="body1" fontWeight="bold">
            {mainTimeLeft <= 60 ? '🚨 Less than 1 minute remaining!' : '⏰ Less than 2 minutes remaining!'}
          </Typography>
          <Typography variant="body2">
            {mainTimeLeft <= 60 
              ? 'Quiz will end automatically when time runs out.'
              : 'Start completing quizlets quickly to maximize your score!'
            }
          </Typography>
        </Alert>
      )}

      {/* Completion Message */}
      {isQuizComplete() && (
        <Box sx={{ 
          mb: 4, 
          p: 3, 
          bgcolor: 'success.light', 
          borderRadius: 2,
          textAlign: 'center',
          border: '2px solid',
          borderColor: 'success.main'
        }}>
          <Typography variant="h5" color="success.contrastText" gutterBottom>
            🎉 All Quizlets Completed!
          </Typography>
          <Typography variant="body1" color="success.contrastText">
            You've completed all {quizData?.quiz_data?.length} quizlets. 
            Click "View Final Results" below to see your overall performance.
          </Typography>
        </Box>
      )}

      {/* Progress Information */}
      {!isQuizComplete() && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {getRemainingQuizlets()} quizlet{getRemainingQuizlets() !== 1 ? 's' : ''} remaining
          </Typography>
        </Box>
      )}

      {/* Quizlet Cards */}
      <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
        {quizData?.quiz_data?.map((quizlet, index) => {
          const status = getQuizletStatus(index);
          const result = quizletResults[index];
          const isDisabled = status === 'completed';

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.6 : 1,
                    transition: 'all 0.3s ease',
                    border: status === 'selected' ? '3px solid' : '1px solid',
                    borderColor: status === 'selected' ? 'primary.main' : 'divider',
                    '&:hover': !isDisabled ? {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                      borderColor: 'primary.main'
                    } : {},
                    position: 'relative'
                  }}
                  onClick={() => !isDisabled && handleQuizletSelect(index)}
                >
                  {/* Status Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: `${getStatusColor(status)}.main`,
                        width: 32,
                        height: 32
                      }}
                    >
                      {getStatusIcon(status)}
                    </Avatar>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    {/* Song Info */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        Quizlet {index + 1}
                      </Typography>
                      
                      {quizlet.song?.image && (
                        <Box sx={{ mb: 2 }}>
                          <img
                            src={quizlet.song.image}
                            alt={quizlet.song.name}
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 8
                            }}
                          />
                        </Box>
                      )}
                      
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {quizlet.song?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {quizlet.song?.artist}
                      </Typography>
                    </Box>

                    {/* Question Count */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Chip
                        label={`${quizlet.questions?.length || 0} Questions`}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    </Box>

                    {/* Results Display */}
                    {result && (
                      <Fade in={true}>
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="body2" color="success.dark" gutterBottom fontWeight="bold">
                            ✅ Completed
                          </Typography>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="success.dark">
                              Score: {formatScore(result.totalScore)}
                            </Typography>
                            <Typography variant="body2" color="success.dark">
                              {result.correctAnswers}/{result.totalQuestions} Correct
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="success.dark">
                            Accuracy: {result.percentage}%
                          </Typography>
                          {result.timedOut && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                              ⏰ Timer expired
                            </Typography>
                          )}
                        </Box>
                      </Fade>
                    )}

                    {/* Action Button */}
                    <Box sx={{ textAlign: 'center' }}>
                      {status === 'completed' ? (
                        <button
                          className="quizlet-button completed"
                          disabled
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #4caf50',
                            borderRadius: '8px',
                            backgroundColor: 'transparent',
                            color: '#4caf50',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <CompletedIcon style={{ fontSize: '20px' }} />
                          Completed
                        </button>
                      ) : status === 'selected' ? (
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Selected - Click "Start Quizlet" below
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Click to select this quizlet
                        </Typography>
                      )}
                    </Box>

                    {/* Completed Badge */}
                    {status === 'completed' && (
                      <Tooltip title="Quizlet completed">
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'success.main',
                            borderRadius: '50%',
                            p: 0.5,
                            boxShadow: 1
                          }}
                        >
                          <CompletedIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                      </Tooltip>
                    )}
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          );
        })}
      </Grid>

      {/* Central Start Button */}
      {selectedQuizletIndex !== null && !isQuizComplete() && (
        <Box sx={{ mt: 4, mb: 3, textAlign: 'center' }}>
          <button
            className="quizlet-button start-main"
            onClick={handleQuizletStart}
            style={{
              padding: '16px 48px',
              border: 'none',
              borderRadius: '12px',
              backgroundColor: '#1976d2',
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              transition: 'all 0.2s ease',
              textTransform: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1565c0';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#1976d2';
              e.target.style.transform = 'translateY(0)';
            }}
           >
             <PlayIcon style={{ fontSize: '24px' }} />
             {mainTimerRunning ? `Continue Quizlet ${selectedQuizletIndex + 1}` : `Start Quizlet ${selectedQuizletIndex + 1}`}
           </button>
        </Box>
      )}

     
    </Box>
  );
};

export default QuizletScreen;
